import typia, { type tags } from 'typia';
import { parseOrNull, type ChartDataPoint, type ChartTimeSeries, type ValidatedChartTimeSeries, } from './chartPayloads';
export const BAR_CHART_FENCE = 'ardoq-chart-bar';
export const PIE_CHART_FENCE = 'ardoq-chart-pie';
export const LINE_CHART_FENCE = 'ardoq-chart-line';
export const STACKED_BAR_CHART_FENCE = 'ardoq-chart-stacked-bar';
export type BarChartPayload = {
    title?: string;
    data: ChartDataPoint[];
};
export type PieChartPayload = {
    title?: string;
    data: ChartDataPoint[];
};
export type LineChartPayload = {
    title?: string;
    data: ChartTimeSeries[];
    has_dynamic_y_axis?: boolean;
};
export type StackedBarChartPayload = {
    title?: string;
    data: ChartTimeSeries[];
    number_slices?: number;
};
type ChartTitle = {
    /** Optional heading rendered above the chart. Omit entirely when the surrounding prose already gives the chart context. */
    title?: string;
};
type ValidatedBarChartPayload = ChartTitle & {
    /** One entry per bar; at least one is required. Provide the categories in the order they should appear. Prefer a small, focused set of bars; group long tails yourself before charting. */
    data: ChartDataPoint[] & tags.MinItems<1>;
};
type ValidatedPieChartPayload = ChartTitle & {
    /** One entry per slice; at least one is required. Each `value` is a part of the whole and the slice is sized by its share of the total. Prefer a small set of slices that sum to a meaningful whole; group long tails into an 'Other' slice yourself before charting. */
    data: ChartDataPoint[] & tags.MinItems<1>;
};
type ValidatedLineChartPayload = ChartTitle & {
    /** One or more series to plot over time; at least one is required. Each series carries its own time-ordered points. Prefer a small, focused set of series; too many lines are hard to read. */
    data: ValidatedChartTimeSeries[] & tags.MinItems<1>;
    /** Scale the y-axis to the data range instead of starting at zero. Enable when the interesting variation is small relative to the absolute values. */
    has_dynamic_y_axis?: boolean;
};
type ValidatedStackedBarChartPayload = ChartTitle & {
    /** One or more series to stack per time bucket; at least one is required. Each series carries its own time-ordered points, and the segments at each time are stacked into a single bar. Prefer a small, focused set of series; too many segments are hard to read. */
    data: ValidatedChartTimeSeries[] & tags.MinItems<1>;
    /** Maximum number of series to show before grouping the rest into a single 'Other' segment. Defaults to 5 when omitted; set it to the series count to show every series. */
    number_slices?: number;
};
/** A bar chart: an optional title and one labeled numeric bar per data point. */
type BarChartInput = ValidatedBarChartPayload & {
    /** Selects a bar chart. Compares a numeric value across categories. */
    chart_type: 'bar';
};
/** A pie chart: an optional title and one labeled slice per data point, sized by proportion. */
type PieChartInput = ValidatedPieChartPayload & {
    /** Selects a pie chart. Shows how a whole splits into proportions. Use only when a single share overwhelmingly dominates; never for close values or many slices. To compare categories, prefer a bar chart. */
    chart_type: 'pie';
};
/** A line chart: an optional title and one or more named series plotted over time. */
type LineChartInput = ValidatedLineChartPayload & {
    /** Selects a line chart. Use it to show one or more metrics over time, where each point sits at a date or timestamp on the x-axis. */
    chart_type: 'line';
};
/** A stacked bar chart: an optional title and one or more named series stacked per time bucket. */
type StackedBarChartInput = ValidatedStackedBarChartPayload & {
    /** Selects a stacked bar chart. Use it to show how a total splits into series at each point in time, where the x-axis is a date or timestamp. Reach for this instead of a line chart when the story is the composition of a total at each date (part-to-whole over time), not the trend of each series on its own. */
    chart_type: 'stacked-bar';
};
export type ChartInput = BarChartInput | PieChartInput | LineChartInput | StackedBarChartInput;
export const CHART_RENDER_SCHEMA = {
    version: 1,
    schema: {
        version: "3.1",
        components: {
            schemas: {
                ChartInput: {
                    oneOf: [
                        {
                            $ref: "#/components/schemas/BarChartInput"
                        },
                        {
                            $ref: "#/components/schemas/PieChartInput"
                        },
                        {
                            $ref: "#/components/schemas/LineChartInput"
                        },
                        {
                            $ref: "#/components/schemas/StackedBarChartInput"
                        }
                    ]
                },
                BarChartInput: {
                    type: "object",
                    properties: {
                        title: {
                            type: "string",
                            description: "Optional heading rendered above the chart. Omit entirely when the surrounding prose already gives the chart context."
                        },
                        data: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/ChartDataPoint"
                            },
                            minItems: 1,
                            description: "One entry per bar; at least one is required. Provide the categories in the order they should appear. Prefer a small, focused set of bars; group long tails yourself before charting."
                        },
                        chart_type: {
                            "const": "bar",
                            description: "Selects a bar chart. Compares a numeric value across categories."
                        }
                    },
                    required: [
                        "data",
                        "chart_type"
                    ],
                    description: "A bar chart: an optional title and one labeled numeric bar per data point."
                },
                ChartDataPoint: {
                    type: "object",
                    properties: {
                        label: {
                            type: "string",
                            description: "Category name shown on the axis, e.g. an application status, lifecycle phase, or type. Keep it short. Long labels are truncated in the chart."
                        },
                        value: {
                            type: "number",
                            description: "Numeric value for this category (the bar height). Must be a plain number, not a formatted string."
                        }
                    },
                    required: [
                        "label",
                        "value"
                    ],
                    description: "A single bar: one category and its numeric value."
                },
                PieChartInput: {
                    type: "object",
                    properties: {
                        title: {
                            type: "string",
                            description: "Optional heading rendered above the chart. Omit entirely when the surrounding prose already gives the chart context."
                        },
                        data: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/ChartDataPoint"
                            },
                            minItems: 1,
                            description: "One entry per slice; at least one is required. Each `value` is a part of the whole and the slice is sized by its share of the total. Prefer a small set of slices that sum to a meaningful whole; group long tails into an 'Other' slice yourself before charting."
                        },
                        chart_type: {
                            "const": "pie",
                            description: "Selects a pie chart. Shows how a whole splits into proportions. Use only when a single share overwhelmingly dominates; never for close values or many slices. To compare categories, prefer a bar chart."
                        }
                    },
                    required: [
                        "data",
                        "chart_type"
                    ],
                    description: "A pie chart: an optional title and one labeled slice per data point, sized by proportion."
                },
                LineChartInput: {
                    type: "object",
                    properties: {
                        title: {
                            type: "string",
                            description: "Optional heading rendered above the chart. Omit entirely when the surrounding prose already gives the chart context."
                        },
                        data: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/ValidatedChartTimeSeries"
                            },
                            minItems: 1,
                            description: "One or more series to plot over time; at least one is required. Each series carries its own time-ordered points. Prefer a small, focused set of series; too many lines are hard to read."
                        },
                        has_dynamic_y_axis: {
                            type: "boolean",
                            description: "Scale the y-axis to the data range instead of starting at zero. Enable when the interesting variation is small relative to the absolute values."
                        },
                        chart_type: {
                            "const": "line",
                            description: "Selects a line chart. Use it to show one or more metrics over time, where each point sits at a date or timestamp on the x-axis."
                        }
                    },
                    required: [
                        "data",
                        "chart_type"
                    ],
                    description: "A line chart: an optional title and one or more named series plotted over time."
                },
                ValidatedChartTimeSeries: {
                    type: "object",
                    properties: {
                        name: {
                            type: "string",
                            description: "Series identifier: what this line measures, e.g. an application count or a risk score. Used to distinguish lines when there is more than one."
                        },
                        label: {
                            type: "string",
                            description: "Optional display name for this series in the legend. Omit to fall back to `name`."
                        },
                        values: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/ChartTimeSeriesPoint"
                            },
                            minItems: 1,
                            description: "Time-ordered points for this series; at least one is required. Provide them in chronological order."
                        }
                    },
                    required: [
                        "name",
                        "values"
                    ],
                    description: "A single line: a named series and its time-ordered points."
                },
                ChartTimeSeriesPoint: {
                    type: "object",
                    properties: {
                        time: {
                            type: "string",
                            description: "A point in time on the x-axis, given as an ISO date or timestamp, e.g. `2024-01`, `2024-01-15`, or `2024-01-15T00:00:00Z`. Points are plotted in chronological order."
                        },
                        value: {
                            type: "number",
                            description: "The value at this time. Must be a plain number, not a formatted string. Required: every point needs a value (nulls/gaps are not supported)."
                        }
                    },
                    required: [
                        "time",
                        "value"
                    ],
                    description: "A single observation in a series: one time and its numeric value."
                },
                StackedBarChartInput: {
                    type: "object",
                    properties: {
                        title: {
                            type: "string",
                            description: "Optional heading rendered above the chart. Omit entirely when the surrounding prose already gives the chart context."
                        },
                        data: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/ValidatedChartTimeSeries"
                            },
                            minItems: 1,
                            description: "One or more series to stack per time bucket; at least one is required. Each series carries its own time-ordered points, and the segments at each time are stacked into a single bar. Prefer a small, focused set of series; too many segments are hard to read."
                        },
                        number_slices: {
                            type: "number",
                            description: "Maximum number of series to show before grouping the rest into a single 'Other' segment. Defaults to 5 when omitted; set it to the series count to show every series."
                        },
                        chart_type: {
                            "const": "stacked-bar",
                            description: "Selects a stacked bar chart. Use it to show how a total splits into series at each point in time, where the x-axis is a date or timestamp. Reach for this instead of a line chart when the story is the composition of a total at each date (part-to-whole over time), not the trend of each series on its own."
                        }
                    },
                    required: [
                        "data",
                        "chart_type"
                    ],
                    description: "A stacked bar chart: an optional title and one or more named series stacked per time bucket."
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/ChartInput"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
} as const;
export const CHART_FENCES: Record<ChartInput['chart_type'], string> = {
    bar: BAR_CHART_FENCE,
    pie: PIE_CHART_FENCE,
    line: LINE_CHART_FENCE,
    'stacked-bar': STACKED_BAR_CHART_FENCE,
};
export const validateBarChartPayload: (value: string) => BarChartPayload | null = parseOrNull((() => { const _io0 = (input: any): boolean => (undefined === input.title || "string" === typeof input.title) && (Array.isArray(input.data) && (1 <= input.data.length && input.data.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem)))); const _io1 = (input: any): boolean => "string" === typeof input.label && "number" === typeof input.value; const __is = (input: any): input is ValidatedBarChartPayload => "object" === typeof input && null !== input && _io0(input); return (input: string): import("typia").Primitive<ValidatedBarChartPayload> | null => { input = JSON.parse(input); return __is(input) ? input as any : null; }; })());
export const validatePieChartPayload: (value: string) => PieChartPayload | null = parseOrNull((() => { const _io0 = (input: any): boolean => (undefined === input.title || "string" === typeof input.title) && (Array.isArray(input.data) && (1 <= input.data.length && input.data.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem)))); const _io1 = (input: any): boolean => "string" === typeof input.label && "number" === typeof input.value; const __is = (input: any): input is ValidatedPieChartPayload => "object" === typeof input && null !== input && _io0(input); return (input: string): import("typia").Primitive<ValidatedPieChartPayload> | null => { input = JSON.parse(input); return __is(input) ? input as any : null; }; })());
export const validateLineChartPayload: (value: string) => LineChartPayload | null = parseOrNull((() => { const _io0 = (input: any): boolean => (undefined === input.title || "string" === typeof input.title) && (Array.isArray(input.data) && (1 <= input.data.length && input.data.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem)))) && (undefined === input.has_dynamic_y_axis || "boolean" === typeof input.has_dynamic_y_axis); const _io1 = (input: any): boolean => "string" === typeof input.name && (undefined === input.label || "string" === typeof input.label) && (Array.isArray(input.values) && (1 <= input.values.length && input.values.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem)))); const _io2 = (input: any): boolean => "string" === typeof input.time && "number" === typeof input.value; const __is = (input: any): input is ValidatedLineChartPayload => "object" === typeof input && null !== input && _io0(input); return (input: string): import("typia").Primitive<ValidatedLineChartPayload> | null => { input = JSON.parse(input); return __is(input) ? input as any : null; }; })());
export const validateStackedBarChartPayload: (value: string) => StackedBarChartPayload | null = parseOrNull((() => { const _io0 = (input: any): boolean => (undefined === input.title || "string" === typeof input.title) && (Array.isArray(input.data) && (1 <= input.data.length && input.data.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem)))) && (undefined === input.number_slices || "number" === typeof input.number_slices); const _io1 = (input: any): boolean => "string" === typeof input.name && (undefined === input.label || "string" === typeof input.label) && (Array.isArray(input.values) && (1 <= input.values.length && input.values.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem)))); const _io2 = (input: any): boolean => "string" === typeof input.time && "number" === typeof input.value; const __is = (input: any): input is ValidatedStackedBarChartPayload => "object" === typeof input && null !== input && _io0(input); return (input: string): import("typia").Primitive<ValidatedStackedBarChartPayload> | null => { input = JSON.parse(input); return __is(input) ? input as any : null; }; })());
